import '../../../features/product-card/product-card-display';
import '../../search/content-card-display';
import searchIcon from '../../../common/icons/search-icon';
import store from '../../../store/store';
import { BaseComponent } from '../../base-component/base-component';
import { Catalog } from '../../../common/constants/catalog';
import { CreateFieldValueFilter } from './interfaces/create-search.interface';
import { CreateSearchAnalytics } from './create-search.analytics';
import { SearchFacet } from '../../search/interfaces/search.interface';
import { StoreState } from '../../../store/store.interface';
import { booleanConverter } from '../../../common/converters';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { html, TemplateResult } from 'lit';
import { performCreateSearch } from './thunks/create-search.thunks';
import { selectCreateFacets, selectCreateSearchTotal } from './store/create-search.selector';
import { setSearchPageType, setSelectedCatalog } from '../../search/store/search.store';
import { smallSpinner } from '../../../common/constants/api-constants';

@customElement('mhecomm-create-search')
export class CreateSearch extends connect(store)(BaseComponent) {
  /**
   * Whether to automatically load search results on page load without waiting for search filters.
   * Only set to true for documentation.
   * It should be false for actual use as default filters are triggering the search.
   */
  @property({ converter: booleanConverter })
  autoLoad = false;

  /**
   * The number of results to load.
   */
  @property({ type: Number })
  pageSize = 10;

  /**
   * The text to display when there is at least one item in the cart.
   */
  @property()
  placeholder = 'Search by ISBN, Title, Author, Keyword';

  /**
   * Parameter to determine the page the search component is getting called, default is empty string.
   */
  @property({ type: String })
  searchPageType = '';

  /**
   * The search query in the input field.
   */
  @property()
  searchQuery = '';

  /**
   * The current store.
   */
  @property()
  store?: string;

  /**
   * set isLoading on search click to show loader on icon
   */
  @state()
  private isLoading = false;

  /**
   * Flag to determine if the search results are being loaded for the first time to used to trigger analytics.
   */
  private isSearchResultsFirstLoad = true;

  connectedCallback(): void {
    super.connectedCallback();

    store.dispatch(setSelectedCatalog(Catalog.he));
    if (this.searchPageType) {
      store.dispatch(setSearchPageType(this.searchPageType));
    }

    if (this.autoLoad) {
      this.search(true);
    }

    this.addEventListener('input', this.updateSearchQuery);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('input', this.updateSearchQuery);
  }

  stateChanged(state: StoreState): void {
    if (this.isSearchResultsFirstLoad) {
      const totals = selectCreateSearchTotal(state);
      if (totals && totals > 0) {
        this.dispatchResultsAnalytics(selectCreateSearchTotal(state));
        this.isSearchResultsFirstLoad = false;
      }
    }
  }

  renderComp(): TemplateResult {
    return html`
      <div class="w-100 input-group" tabindex="-1" role="region" aria-label="Search">
        <input
          aria-label="search"
          id="input-search-box"
          data-automation-id="input-search-box"
          class="rounded-start form-control ms-0"
          type="text"
          placeholder=${this.placeholder}
          .value=${this.searchQuery}
          @keypress=${this.keyPress}
        />
        <button
          id="submit-search-button"
          data-automation-id="submit-search-button"
          class="btn btn-primary"
          aria-label="Search Submit"
          @click=${() => this.search(false)}
        >
          ${this.isLoading ? smallSpinner() : searchIcon}
        </button>
      </div>
    `;
  }

  private keyPress(event: KeyboardEvent): void {
    if (event.which === 13 || event.keyCode === 13 || event.key === 'Enter') {
      this.search(false);
      if (event.key === 'Enter') {
        this.inputElement().blur();
      }
    }
  }

  private updateSearchQuery(): void {
    this.searchQuery = this.inputElement().value;
  }

  private async search(isPageLoad: boolean): Promise<void> {
    if (!isPageLoad) {
      CreateSearchAnalytics.search({
        element: this,
        id: this.id,
        searchPageType: this.searchPageType,
        storeCategory: this.store,
        term: this.searchQuery,
      });
    }

    await this.loadSearchResults({});
  }

  async loadSearchResults({ searchQuery = '', page = 1 }): Promise<void> {
    const result = await store.dispatch(
      performCreateSearch({
        searchValue: searchQuery || this.searchQuery,
        pageSize: this.pageSize,
        start: page,
        facets: true,
        store: this.store,
        fieldValueFilter: this.getAppliedFilterValues(),
        excludeFacetName: undefined,
        clearFilter: true,
      })
    );

    this.dispatchResultsAnalytics(result.total);
  }

  /**
   * Applies the selected filter values when searching a term.
   */
  private getAppliedFilterValues(): CreateFieldValueFilter {
    const appliedFilterValues: CreateFieldValueFilter = {};
    const facets: SearchFacet[] = selectCreateFacets(store.getState() as StoreState)!;

    facets?.forEach((facet): void => {
      const values: Array<string> = facet?.facetValues
        .filter((facetValue) => facetValue.selected)
        .map((val) => val.name);

      if (values.length > 0) {
        appliedFilterValues[facet.name] = values;
      }
    });

    return appliedFilterValues;
  }

  private dispatchResultsAnalytics(total?: number): void {
    CreateSearchAnalytics.searchResults({
      element: this,
      id: this.id,
      searchPageType: this.searchPageType,
      storeCategory: this.store,
      term: this.searchQuery,
      totalResults: total ?? 0,
    });
  }

  private inputElement(): HTMLInputElement {
    return this.shadowRoot!.getElementById('input-search-box')! as HTMLInputElement;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-create-search': CreateSearch;
  }
}
